import { useGetClaim, useGetPortals } from '@/api'
import { ROUTER_PATHS } from '@/lib/constants'
import {
  formatUsPhoneToHtml,
  getContactEmails,
  getContactPhoneNumber,
} from '@eigtech/contacts-util'
import { PortalDeactivatedCodeEnumSchema, PortalStatusSchema } from '@eigtech/portals-types'
import { getContactName, useGetContactOfTypeFromClaim } from '@eigtech/ui-shared-contacts'
import {
  Badge,
  Box,
  CircularProgress,
  FancyLink,
  HStack,
  Heading,
  Icon,
  IconProps,
  Page,
  PageTabs,
  Skeleton,
  Stack,
  TabList,
  Text,
  forwardRef,
  getGoogleMapQueryLink,
  useBreakpointValue,
} from '@eigtech/ui-shared-dave'
import { formatPhoneNumber } from '@eigtech/ui-shared-phone'
import { BackButton, Link, LinkButton, LinkTab } from '@eigtech/ui-shared-router'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { Button } from '@eigtech/ui/components/button'
import { Outlet, createFileRoute, useChildMatches } from '@tanstack/react-router'
import { HTTPError } from 'ky'
import { TriangleAlert } from 'lucide-react'
import { useMemo } from 'react'
import { FaMapMarkedAlt } from 'react-icons/fa'
import { IoMdDocument } from 'react-icons/io'
import {
  MdCalendarMonth,
  MdInfo,
  MdOutlineStickyNote2,
  MdMeetingRoom,
  MdPeople,
  MdPhoneCallback,
  MdPhoto,
  MdSearch,
} from 'react-icons/md'
import { TbWorldDollar } from 'react-icons/tb'

export const Route = createFileRoute('/_auth/claims/$claimNumber/_layout')({
  component: ClaimDetail,
})

function ClaimDetail() {
  const { claimNumber } = Route.useParams()

  const { isError, error, status } = useGetClaim(claimNumber)

  const backButton = useBreakpointValue({
    base: null,
    md: <BackButton to={ROUTER_PATHS.claims.route}>← Claims</BackButton>,
  })

  const isForbiddenError = useMemo(
    () => error && error instanceof HTTPError && error.response.status === 403,
    [error]
  )

  const { data: portals } = useGetPortals()
  const claimClosed = useMemo(
    () =>
      isForbiddenError &&
      portals?.some(
        (portal) =>
          portal.entityId === claimNumber &&
          portal.entityType === 'claim' &&
          portal.status === PortalStatusSchema.enum.INACTIVE &&
          portal.deactivatedCode === PortalDeactivatedCodeEnumSchema.enum.ENTITY_CLOSED
      ),
    [isForbiddenError, portals, claimNumber]
  )

  if (claimClosed) {
    return (
      <Alert className="mx-auto max-w-xl" variant="destructive">
        <AlertIcon>
          <TriangleAlert />
        </AlertIcon>
        <AlertContent>
          <AlertTitle>Claim {claimNumber} Closed</AlertTitle>
          <AlertDescription>
            <p>
              This claim has been closed. Please contact the EIG home office to re-open this claim,
              if needed.
            </p>

            <Button className="mt-1" render={<Link to={ROUTER_PATHS.claims.route} />} size="sm">
              ← Claims
            </Button>
          </AlertDescription>
        </AlertContent>
      </Alert>
    )
  }

  if (isError) {
    return (
      <Alert className="mx-auto my-8 max-w-xl" variant="destructive">
        <AlertIcon>
          <TriangleAlert />
        </AlertIcon>
        <AlertContent>
          <AlertTitle>Could not retrieve claim: {claimNumber}</AlertTitle>
          <AlertDescription>
            <p>
              Please try again by refreshing the page. If the issue persists, please contact EIG
              Customer Service.
            </p>

            <Button className="mt-1" render={<Link to={ROUTER_PATHS.claims.route} />} size="sm">
              ← Claims
            </Button>
          </AlertDescription>
        </AlertContent>
      </Alert>
    )
  }

  return (
    <Page
      aboveHeader={backButton}
      belowHeader={<ClaimDetailMenu />}
      heading={<ClaimDetailHeader />}
      status={status}
    >
      <Outlet />
    </Page>
  )
}

function ClaimDetailHeader() {
  const { claimNumber } = Route.useParams()

  const { data: claim, isPending, isFetching, isError } = useGetClaim(claimNumber)

  const { contacts: policyHolderContacts } = useGetContactOfTypeFromClaim({
    contactType: 'policyHolder',
    claimNumber,
  })
  const policyHolder = policyHolderContacts?.[0]

  const { contacts: primaryContacts } = useGetContactOfTypeFromClaim({
    contactType: 'primaryContact',
    claimNumber,
  })
  const primaryContact = primaryContacts?.[0]

  const contact = policyHolder ?? primaryContact

  const name = getContactName(contact ?? {})
  const phone = getContactPhoneNumber(contact ?? {})
  const emails = getContactEmails(contact ?? {})

  const { addressLine1, addressLine2, city, stateOrProvince, zipOrPostalCode } =
    claim?.lossLocation?.address ?? {}
  const fullAddress = [addressLine1, addressLine2, city, stateOrProvince, zipOrPostalCode]
    .filter((value) => !!value)
    .join(', ')

  return (
    <Stack spacing="1">
      <HStack>
        <Skeleton isLoaded={!isPending}>
          <Heading as="h1" size="lg">
            {claim ? name : isPending}
          </Heading>
        </Skeleton>

        {claim?.isReadOnly && <Badge colorScheme="yellow">Read-only Claim</Badge>}

        {!!(isFetching || isPending) && <CircularProgress isIndeterminate size="2rem" />}
      </HStack>

      <Stack spacing="0">
        <Text>{claimNumber}</Text>

        {!isError && (
          <>
            <Skeleton isLoaded={!isPending}>
              {isPending
                ? 'Loading phone...'
                : !!phone && (
                    <FancyLink href={`tel:${formatUsPhoneToHtml(phone)}`} iconPosition="left">
                      {formatPhoneNumber(phone)}
                    </FancyLink>
                  )}
            </Skeleton>
            <Skeleton isLoaded={!isPending}>
              {isPending
                ? 'Loading email...'
                : !!emails &&
                  emails.map((email) => (
                    <FancyLink key={email} href={`mailto:${email}`} iconPosition="left">
                      {email}
                    </FancyLink>
                  ))}
            </Skeleton>
            <Skeleton isLoaded={!isPending}>
              {claim?.lossLocation?.address ? (
                <FancyLink
                  customIcon={MapIcon}
                  href={getGoogleMapQueryLink(claim.lossLocation.address)}
                  iconPosition="left"
                  isExternal
                >
                  {fullAddress}
                </FancyLink>
              ) : (
                'Loading...'
              )}
            </Skeleton>
          </>
        )}
      </Stack>
    </Stack>
  )
}

function ClaimDetailMenu() {
  const { claimNumber } = Route.useParams()

  const { error } = useGetClaim(claimNumber)

  const matches = useChildMatches()
  const tabIndex =
    routes.findIndex((route) => matches.some((match) => match.routeId === route.id)) ?? 0

  const isClaimHome = matches.some(
    (match) => match.routeId === '/_auth/claims/$claimNumber/_layout/'
  )

  if (error && error instanceof HTTPError && error.response.status === 403) {
    return null
  }

  return (
    <>
      <Box bg="white" borderRadius="md" display={{ base: 'block', md: 'none' }}>
        <LinkButton
          params={{ claimNumber }}
          size="sm"
          to={isClaimHome ? ROUTER_PATHS.claims.route : ROUTER_PATHS.claimDetail.route}
          variant="outline"
          w="full"
        >
          ← {isClaimHome ? 'All Claims' : 'Claim Home'}
        </LinkButton>
      </Box>

      <Box display={{ base: 'none', md: 'block' }}>
        <PageTabs index={tabIndex}>
          <TabList>
            {routes.map((tab) => (
              <LinkTab
                key={tab.id}
                data-id={tab.id}
                params={{ claimNumber }}
                position="relative"
                {...tab.url}
              >
                {tab.label}
              </LinkTab>
            ))}
          </TabList>
        </PageTabs>
      </Box>
    </>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const routes = [
  {
    url: { to: ROUTER_PATHS.claimDetailInfo.route },
    id: ROUTER_PATHS.claimDetailInfo.routeId,
    label: 'Details',
    icon: MdInfo,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailSchedule.route },
    id: ROUTER_PATHS.claimDetailSchedule.routeId,
    label: 'Schedule',
    icon: MdCalendarMonth,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailEstimates.route },
    id: ROUTER_PATHS.claimDetailEstimates.routeId,
    label: 'Estimates',
    icon: TbWorldDollar,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailContacts.route },
    id: ROUTER_PATHS.claimDetailContacts.routeId,
    label: 'Contacts',
    icon: MdPeople,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailDocuments.route },
    id: ROUTER_PATHS.claimDetailDocuments.routeId,
    label: 'Documents',
    icon: IoMdDocument,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailMedia.route },
    id: ROUTER_PATHS.claimDetailMedia.routeId,
    label: 'Media',
    icon: MdPhoto,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailNotes.route },
    id: ROUTER_PATHS.claimDetailNotes.routeId,
    label: 'Notes',
    icon: MdOutlineStickyNote2,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailCallbacks.route },
    id: ROUTER_PATHS.claimDetailCallbacks.routeId,
    label: 'Callbacks',
    icon: MdPhoneCallback,
  },
  {
    url: { to: ROUTER_PATHS.claimDetailMeetings.route },
    id: ROUTER_PATHS.claimDetailMeetings.routeId,
    label: 'Inspections',
    icon: MdMeetingRoom,
    permissions: { action: 'create', subject: 'meeting' },
  },
  {
    url: { to: ROUTER_PATHS.claimDetailInspections.route },
    id: ROUTER_PATHS.claimDetailInspections.routeId,
    label: 'Inspections (V2)',
    icon: MdSearch,
  },
  // Now generating loss reports with guided documents
  // TODO: Delete related page and components in 3-6 months
  // {
  //   url: { to: ROUTER_PATHS.claimDetailLossReport.route },
  //   id: ROUTER_PATHS.claimDetailLossReport.routeId,
  //   label: 'Loss Report',
  //   icon: MdAssignment,
  // },
]

const MapIcon = forwardRef<IconProps, 'svg'>((props, ref) => (
  <Icon ref={ref} {...props} as={FaMapMarkedAlt} />
))
